import React, { useState } from "react";
import { API } from "aws-amplify";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

export default function EmailTransactionResend() {
    const [emailAddress, setEmailAddress] = useState("");
    const [alternativeSendToEmailAddress, setAlternativeSendToEmailAddress] = useState("");

    const [transactions, setTransactions] = useState([]);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isResending, setIsResending] = useState(false);

    function validateEmail(email) {
        return /\S+@\S+\.\S+/.test(email.trim());
    }

    function getTransactionsByEmail(email) {
        return API.post("aires", "/tranadmin", {
            body: {
                emailLookup: true,
                emailAddress: email,
            },
        });
    }

    function resendConfirmation(transaction) {
        return API.post("aires", "/tranadmin", {
            body: {
                resendConfirmation: true,
                pk: transaction.pk,
                sk: transaction.sk,
                itemId: transaction.itemId,
                emailAddress:
                    alternativeSendToEmailAddress.trim().toLowerCase() ||
                    transaction.email ||
                    transaction.GSI3SK ||
                    emailAddress.trim().toLowerCase(),
            },
        });
    }

    function getTransactionDate(transaction) {
        if (!transaction.createdAt) return "Unknown date";

        return new Date(transaction.createdAt).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    function getTransactionSite(transaction) {
        return (
            transaction.content?.metadata?.siteName ||
            transaction.content?.siteName ||
            transaction.content?.metadata?.siteCode ||
            transaction.itemCode ||
            "Unknown site"
        );
    }

    function getTransactionDates(transaction) {
        const dates = transaction.content?.metadata?.dates || "Unknown dates";
        if (dates !== "Unknown dates") {
            const datesArr = dates.split(",");
            if (datesArr.length === 1) return dates;
            return `${datesArr[0]} - ${datesArr[datesArr.length - 1]}`;
        }
        return dates;
    }

    function getTransactionId(transaction) {
        return (
            transaction.GSI1SK ||
            transaction.itemId ||
            transaction.sk ||
            "Unknown transaction"
        );
    }

    function getTransactionPaymentStatus(transaction) {
        return (
            transaction.content?.payment_status ||
            transaction.content?.metadata?.payment_status ||
            transaction.payment_status ||
            "unknown"
        );
    }

    function isPaidTransaction(transaction) {
        return getTransactionPaymentStatus(transaction) === "paid";
    }

    function getTransactionFee(transaction) {
        return transaction.fee !== undefined && transaction.fee !== null
            ? `£${transaction.fee}`
            : "Unknown fee";
    }

    function getTransactionReg(transaction) {
        return (
            transaction.vreg ||
            transaction.reg ||
            transaction.content?.metadata?.reg ||
            ""
        );
    }

    function getTransactionTitle(transaction) {
        return `${getTransactionId(
            transaction
        )}`;
    }

    function getTransactionSubtitle(transaction) {
        const reg = getTransactionReg(transaction);
        const dates = getTransactionDates(transaction);
        return `${getTransactionDates(transaction)} • ${getTransactionPaymentStatus(
            transaction
        )} • ${getTransactionFee(transaction)}${reg ? ` • ${reg.toUpperCase()}` : ""
            }`;
    }

    async function handleSearch(event) {
        event.preventDefault();

        if (!validateEmail(emailAddress)) {
            alert("Please enter a valid email address.");
            return;
        }

        setIsSearching(true);
        setHasSearched(true);
        setTransactions([]);
        setSelectedTransaction(null);
        setConfirmOpen(false);

        try {
            const result = await getTransactionsByEmail(
                emailAddress.trim().toLowerCase()
            );

            if (result.status) {
                console.log(result.data?.trans || []);
                setTransactions(result.data?.trans || []);
            } else {
                setTransactions([]);
            }
        } catch (err) {
            console.error("Transaction lookup failed:", err);
            alert("Unable to search for transactions.");
        } finally {
            setIsSearching(false);
        }
    }

    function handleTransactionClick(transaction) {
        if (!isPaidTransaction(transaction)) return;

        setSelectedTransaction(transaction);
        setConfirmOpen(true);
    }

    function handleConfirmClose() {
        if (isResending) return;

        setConfirmOpen(false);
    }

    async function handleConfirmResend() {
        if (!selectedTransaction) return;

        if (!isPaidTransaction(selectedTransaction)) {
            alert("Cannot resend confirmation for an unpaid transaction.");
            setConfirmOpen(false);
            return;
        }

        setIsResending(true);

        try {
            const result = await resendConfirmation(selectedTransaction);

            if (result.status) {
                alert("Confirmation resend requested.");
                setConfirmOpen(false);
            } else {
                alert("Unable to resend confirmation: " + result.error);
            }
        } catch (err) {
            console.error("Resend confirmation failed:", err);
            alert("Unable to resend confirmation.");
        } finally {
            setIsResending(false);
        }
    }

    return (
        <>
            <Box mt={5} mx={2} display="flex" justifyContent="center" width="100%">
                <Box maxWidth="650px" width="100%">
                    <Paper elevation={1}>
                        <Box p={3}>
                            <Typography variant="h6" style={{ color: "#10385A" }}>
                                Resend Booking Confirmation
                            </Typography>

                            <Typography
                                variant="body2"
                                style={{
                                    color: "#10385A",
                                    marginTop: "8px",
                                    marginBottom: "16px",
                                }}
                            >
                                Search by guest email address, then click a paid transaction to
                                resend the booking confirmation.
                            </Typography>

                            <form onSubmit={handleSearch}>
                                <Box display="flex" flexWrap="wrap" alignItems="center">
                                    <Box flex="1 1 280px">
                                        <TextField
                                            label="Guest email address"
                                            variant="outlined"
                                            fullWidth
                                            value={emailAddress}
                                            onChange={(e) => setEmailAddress(e.target.value)}
                                            disabled={isSearching || isResending}
                                        />
                                    </Box>

                                    <Box ml={2} my={1}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            disabled={!validateEmail(emailAddress) || isSearching || isResending}
                                        >
                                            {isSearching ? "Searching..." : "Search"}
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                            <Box flex="1 1 280px" mt={2}>
                                <TextField
                                    label="Alternative send-to email address (optional)"
                                    variant="outlined"
                                    fullWidth
                                    value={alternativeSendToEmailAddress}
                                    onChange={(e) => setAlternativeSendToEmailAddress(e.target.value)}
                                    disabled={isSearching || isResending}
                                />
                            </Box>


                            {isSearching && (
                                <Box mt={3} display="flex" justifyContent="center">
                                    <CircularProgress size={24} />
                                </Box>
                            )}

                            {!isSearching && hasSearched && transactions.length === 0 && (
                                <Box mt={3}>
                                    <Typography variant="body2" style={{ color: "#10385A" }}>
                                        No transactions found for that email address.
                                    </Typography>
                                </Box>
                            )}

                            {!isSearching && transactions.length > 0 && (
                                <Box mt={3}>
                                    <Typography variant="subtitle1" style={{ color: "#10385A" }}>
                                        Transactions found: {transactions.length}
                                    </Typography>

                                    <List>
                                        {transactions.map((transaction, index) => {
                                            const paid = isPaidTransaction(transaction);

                                            return (
                                                <React.Fragment
                                                    key={`${transaction.pk || "pk"}|${transaction.sk || transaction.itemId || index
                                                        }`}
                                                >
                                                    <ListItem
                                                        button
                                                        disabled={!paid}
                                                        onClick={() => handleTransactionClick(transaction)}
                                                    >
                                                        <ListItemText
                                                            primary={`${getTransactionTitle(transaction)}  (${getTransactionDate(transaction)})`}
                                                            secondary={
                                                                paid ? (
                                                                    getTransactionSubtitle(transaction)
                                                                ) : (
                                                                    <>
                                                                        {/*getTransactionSubtitle(transaction)*/}
                                                                        {/*<br />*/}
                                                                        <span style={{ color: "#ff1e00" }}>
                                                                            Cannot resend -  unpaid transaction
                                                                        </span>
                                                                    </>
                                                                )
                                                            }
                                                        />
                                                    </ListItem>
                                                    {index < transactions.length - 1 && <Divider />}
                                                </React.Fragment>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Box>

            <Dialog
                open={confirmOpen}
                onClose={handleConfirmClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Resend booking confirmation?</DialogTitle>

                <DialogContent>
                    {selectedTransaction && (
                        <>
                            <DialogContentText>
                                Please confirm you want to resend the booking confirmation for
                                this transaction.
                            </DialogContentText>

                            <Box mt={2}>
                                <Typography variant="body2">
                                    <strong>Site:</strong> {getTransactionSite(selectedTransaction)}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Transaction:</strong>{" "}
                                    {getTransactionId(selectedTransaction)}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Date:</strong>{" "}
                                    {getTransactionDate(selectedTransaction)}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Booked date/s:</strong>{" "}
                                    {getTransactionDates(selectedTransaction)}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Email:</strong>{" "}
                                    {selectedTransaction.email ||
                                        selectedTransaction.GSI3SK ||
                                        emailAddress.trim().toLowerCase()}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Status:</strong>{" "}
                                    {getTransactionPaymentStatus(selectedTransaction)}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Fee:</strong> {getTransactionFee(selectedTransaction)}
                                </Typography>

                                {getTransactionReg(selectedTransaction) && (
                                    <Typography variant="body2">
                                        <strong>Reg:</strong>{" "}
                                        {getTransactionReg(selectedTransaction).toUpperCase()}
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleConfirmClose} disabled={isResending}>
                        Cancel
                    </Button>

                    <Button
                        onClick={handleConfirmResend}
                        color="secondary"
                        variant="contained"
                        disabled={isResending}
                    >
                        {isResending ? "Sending..." : "Confirm Resend"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}